import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center rounded border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        // ATEK Legacy-style badges (Bootstrap-like)
        default:
          'border-transparent bg-atek-blue text-white',
        secondary:
          'border-atek-grey-border bg-atek-grey-bg text-foreground',
        destructive:
          'border-transparent bg-danger text-white',
        outline:
          'border-atek-grey-border bg-transparent text-foreground',
        // Status badges - legacy Bootstrap colors
        success:
          'border-transparent bg-success-light text-success-dark',
        warning:
          'border-transparent bg-warning-light text-warning-dark',
        info:
          'border-transparent bg-info-light text-info-dark',
        danger:
          'border-transparent bg-red-100 text-red-700',
        // Purple badge — used for "Unmanaged" sensor status to match the
        // V2 customer dashboard color scheme.
        purple:
          'border-transparent bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
        // Orange badge — used for "Alerts Inhibited" so it stays distinct
        // from yellow "To Acknowledge".
        orange:
          'border-transparent bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
        // Yellow badge — used for "To Acknowledge" (status=4). Distinct
        // from `warning` (which Bootstrap renders amber) and from `orange`
        // (used for Alerts Inhibited).
        yellow:
          'border-transparent bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
        // Red badge — used for "In Alert" (status 2/3 — reading out of safe
        // range). Distinct from `danger` (pale red) — solid red so live
        // alarms pop against the rest of the table.
        red:
          'border-transparent bg-red-500 text-white dark:bg-red-600 dark:text-red-50',
        // Gray badge — used for "In Maintenance" (lighter gray to match the
        // wrench icon in the V2 legend).
        gray:
          'border-transparent bg-gray-200 text-gray-700 dark:bg-gray-700/40 dark:text-gray-200',
        // Dark gray badge — used for "Inactive" (Communication Lost),
        // matches the dark-clock icon in the V2 legend.
        darkgray:
          'border-transparent bg-gray-500 text-white dark:bg-gray-600 dark:text-gray-100',
        // Blue-gray badge — used for "Archived" sensor status to match the
        // V2 customer dashboard's slate-blue tone (solid, not pale).
        blue:
          'border-transparent bg-slate-500 text-white dark:bg-slate-600 dark:text-slate-100',
        // Solid status badges
        'success-solid':
          'border-transparent bg-success text-white',
        'warning-solid':
          'border-transparent bg-warning text-white',
        'info-solid':
          'border-transparent bg-info text-white',
        'danger-solid':
          'border-transparent bg-danger text-white',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
